/**
 * Animation preset resolution — pure functions, no DOM.
 *
 * Presets are loaded once from a data-presets JSON attribute and cached.
 * Expression resolution converts strings like "-(ov.x + ov.w)" into numbers
 * using overlay position and canvas dimensions.
 */

interface PresetDef {
  group: string;
  /** Le même type de l'autre côté (fadeIn ↔ fadeOut) : une sortie est l'entrée à l'envers. */
  pair?: string;
  primitive: string;
  from: number | string;
  to: number | string;
  duration: number | string;
  easing: string;
  /** Les réglages propres au preset (la couleur d'un balayage). */
  options?: string[];
}

interface Anim {
  preset: string;
  primitive?: string;
  from?: number | string;
  to?: number | string;
  duration?: number | string;
  delay?: number;
  easing?: string;
  fromEnd?: boolean;
  _fromEndDelay?: number;
}

interface Overlay {
  animations?: Anim[];
  position?: { x?: number | string; y?: number | string; width?: number | string; height?: number | string };
  start?: number;
  end?: number;
  _cvW?: number;
  _cvH?: number;
}

interface Composition {
  width?: number;
  height?: number;
}

type PresetMap = Record<string, PresetDef>;

let _cache: PresetMap | null = null;

export function loadPresets(jsonOrObj: string | PresetMap): void {
  _cache = typeof jsonOrObj === "string" ? JSON.parse(jsonOrObj) : jsonOrObj;
}

export function presets(): PresetMap {
  return _cache || {};
}

export function preset(name: string): PresetDef | null {
  return _cache?.[name] || null;
}

export function isExpression(val: unknown): val is string {
  return typeof val === "string" && /[a-z]/.test(val);
}

export function recalcAnimations(overlay: Overlay): void {
  if (!overlay?.animations) return;
  for (const anim of overlay.animations) {
    const def = _cache?.[anim.preset];
    if (!def) continue;
    if (isExpression(def.from)) anim.from = resolveValue(def.from, overlay);
    if (isExpression(def.to)) anim.to = resolveValue(def.to, overlay);
    if (isExpression(def.duration)) anim.duration = resolveValue(def.duration, overlay);
  }
}

export function resolveValue(expr: number | string, overlay?: Overlay, composition?: Composition): number {
  if (typeof expr === "number") return expr;
  if (typeof expr !== "string") return 0;

  const cvW = composition?.width || overlay?._cvW || 1080;
  const cvH = composition?.height || overlay?._cvH || 1920;
  const pos = overlay?.position || {};

  const vars: Record<string, number> = {
    "ov.duration": Math.max(0.1, (overlay?.end ?? 0) - (overlay?.start ?? 0)),
    "ov.x": px(pos.x, cvW),
    "ov.y": px(pos.y, cvH),
    "ov.w": px(pos.width, cvW),
    "ov.h": px(pos.height, cvH),
    "cv.w": cvW,
    "cv.h": cvH,
  };

  let resolved = expr;
  for (const [key, val] of Object.entries(vars)) {
    resolved = resolved.replaceAll(key, String(val));
  }

  try {
    if (!/^[\d\s+\-*/().]+$/.test(resolved)) return 0;
    return Function(`"use strict"; return (${resolved})`)();
  } catch {
    return 0;
  }
}

function px(val: number | string | undefined, ref: number): number {
  if (typeof val === "number") return val;
  if (typeof val === "string" && val.endsWith("%")) return (parseFloat(val) / 100) * ref;
  return parseFloat(val as string) || 0;
}
